import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useParams} from "react-router-dom";

import {service as SurveyService} from "../Services/SurveyService";

import {ObjectUtils} from '../Utils/ObjectUtils';
import {usePermission} from "./PermissionHooks";
import {SET_TOOLBARS} from "../Redux/Actions/Types";

import {ProgramsViewPage} from '../Components/Pages/Programs/ViewPage';
import {ProgramsIndexPage} from '../Components/Pages/Programs/IndexPage';
import {FilesIndexPage} from '../Components/Pages/Files/FilesIndexPage';
import {ProgramsTrackingPage} from '../Components/Pages/Programs/TrackingPage';
import {ProjectsViewPage} from '../Components/Pages/Projects/ViewPage';
import {ContactsIndexPage} from '../Components/Pages/Contacts/IndexPage';
import {PermitsIndexPage} from '../Components/Pages/Permits/IndexPage';
import {ContextNodeActivityPage} from '../Components/Pages/Activity/ActivityPage';
import {ConservationActionsViewPage} from '../Components/Pages/ConservationActions/ViewPage';
import {EventsIndexPage} from "../Components/Pages/Events/IndexPage";
import {SampleEventViewPage} from "../Components/Pages/Events/ViewPage";
import {PhotoComparisonPage} from "../Components/Pages/Photos";
import {SurveyPage} from "../Components/Pages/ConservationActions/SurveyPage";
import {LedgerPage} from "../Components/Pages/Ledger/LedgerPage";


export const useTools = () => {
    const { cProgId, conActId, pProgId, projId, sampleEventId } = useParams();
    
    const sampleEventTools = useSampleEventTools(sampleEventId);
    const conservationActionTools = useConservationActionTools(conActId);
    const projectTools = useProjectTools(projId);
    const subProgramTools = useSubProgramTools(cProgId);
    const programTools = useProgramTools(pProgId);
    const rootTools = useRootTools();

    if (sampleEventId) {
        return sampleEventTools;
    } else if (conActId) {
        return conservationActionTools;
    } else if (projId) {
        return projectTools;
    } else if (cProgId) {
        return subProgramTools;
    } else if (pProgId) {
        return programTools;
    } else {
        return rootTools;
    }
};

export const useToolBuilder = () => {
    const params = useParams();
    const tools = useTools();
    const dispatch = useDispatch();

    return () => {
        let newContext = {
            isValid: true,
            component: null,
            key: null
        };

        let tool = null;
        if(tools && tools.length) {
            if(params?.tool) {
                tool = tools.find((x) => x.key.toLowerCase() === params.tool.toLowerCase());
            } else {
                //if tool param doesn't exist lets use the second to last assuming we have something like /{context}/{hardcodedTool}/{id}
                let tokens = window.location.pathname.split('/');
                if(tokens.length > 1) {
                    let param = tokens[tokens.length-2];
                    tool = tools.find((x) => x.key.toLowerCase() === param);
                }
            }
        }

        let toolbars = [];
        tools.forEach((x) => {
            toolbars.push({
                title: x.title,
                active: x.key === (tool ? tool.key : ""),
                key: x.key,
                display: x.display,
                disabled: x.disabled,
                toolbarItemComponent: x.toolbarItemComponent,
            });
        });

        if(tool) {
            newContext.component = tool.component;
            newContext.key = tool.key;
        }

        dispatch({type: SET_TOOLBARS, toolbars});

        return newContext;
    };
};


const useRootTools = () => {
    const permission = usePermission();

    return [
        {
            key: "programs",
            title: "Programs",
            component: ProgramsIndexPage,
            display: true,
            isDefault: true
        },
        {
            key: "contacts",
            title: "Contacts",
            component: ContactsIndexPage,
            display: permission('view_contacts'),
            isDefault: false
        },
        {
            key: "events",
            title: "Events",
            component: EventsIndexPage,
            display: permission('view_sample_events'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "files",
            title: "Files",
            component: FilesIndexPage,
            display: permission('view_files_tool'),
            isDefault: false
        },
        {
            key: "activity",
            title: "Activity",
            component: ContextNodeActivityPage,
            display: permission('view_root_activity_feed_events'),
            isDefault: false
        }
    ];
};

const useProgramTools = (slug) => {
    const permission = usePermission();

    let ret = [
        {
            key: "summary",
            title: "Summary",
            component: ProgramsViewPage,
            display: true,
            isDefault: true,
            disabled: false,
        },
        {
            key: "contacts",
            title: "Contacts",
            component: ContactsIndexPage,
            display: permission('view_contacts'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "tracking",
            title: "Tracking",
            component: ProgramsTrackingPage,
            display: true,
            isDefault: false,
            disabled: false,
        },
        {
            key: "ledger",
            title: "Ledger",
            component: LedgerPage,
            display: true,
            isDefault: false,
            disabled: false,
        },
        {
            key: "events",
            title: "Events",
            component: EventsIndexPage,
            display: permission('view_sample_events'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "files",
            title: "Files",
            component: FilesIndexPage,
            display: permission('view_files_tool'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "permit",
            title: "Permits",
            component: PermitsIndexPage,
            display: permission('view_survey') && permission('view_permits'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "activity",
            title: "Activity",
            component: ContextNodeActivityPage,
            display: permission('view_program_activity_feed_events'),
            isDefault: false,
            disabled: false,
        }
    ];

    // unsaved models only have the summary tool
    if(slug === 'new') {
        return [ret[0]]
    }

    return ret;
};

const useSubProgramTools = (slug) => {
    return useProgramTools(slug).filter((x) => x.key !== "tracking");
};

const useProjectTools = (slug) => {
    const permission = usePermission();

    let ret = [
        {
            key: "summary",
            title: "Summary",
            component: ProjectsViewPage,
            display: true,
            isDefault: true,
            disabled: false,
        },
        {
            key: "contacts",
            title: "Contacts",
            component: ContactsIndexPage,
            display: permission('view_contacts'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "events",
            title: "Events",
            component: EventsIndexPage,
            display: permission('view_sample_events'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "files",
            title: "Files",
            component: FilesIndexPage,
            display: permission('view_files_tool'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "permit",
            title: "Permits",
            component: PermitsIndexPage,
            display: permission('view_survey') && permission('view_permits'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "photos",
            title: "Photos",
            component: PhotoComparisonPage,
            display: permission('view_photos'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "activity",
            title: "Activity",
            component: ContextNodeActivityPage,
            display: permission('view_project_activity_feed_events'),
            isDefault: false,
            disabled: false,
        }
    ];

    // unsaved models only have the summary tool
    if(slug === 'new') {
        return [ret[0]]
    }

    return ret;
};

const useConservationActionTools = (slug) => {
    const permission = usePermission();

    let ret = [
        {
            key: "summary",
            title: "Summary",
            component: ConservationActionsViewPage,
            display: true,
            isDefault: true,
            disabled: false,
        },
        {
            key: "eligibility",
            title: "Eligibility",
            component: SurveyPage,
            display: true,
            isDefault: false,
            disabled: true,
            toolbarItemComponent: 'SurveyToolbarItem',
        },
        {
            key: "design",
            title: "Design",
            component: SurveyPage,
            display: true,
            isDefault: false,
            disabled: true,
            toolbarItemComponent: 'SurveyToolbarItem',
        },
        {
            key: "permit",
            title: "Permits",
            component: SurveyPage,
            display: permission('view_permits'),
            isDefault: false,
            disabled: true,
            toolbarItemComponent: 'SurveyToolbarItem',
        },
        {
            key: "as-built",
            title: "As Built",
            component: SurveyPage,
            display: true,
            isDefault: false,
            disabled: true,
            toolbarItemComponent: 'SurveyToolbarItem',
        },
        {
            key: "events",
            title: "Events",
            component: EventsIndexPage,
            display: permission('view_sample_events'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "files",
            title: "Files",
            component: FilesIndexPage,
            display: permission('view_files_tool'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "activity",
            title: "Activity",
            component: ContextNodeActivityPage,
            display: permission('view_conservation_action_activity_feed_events'),
            isDefault: false,
            disabled: false,
        }
    ];

    // unsaved models only have the summary tool
    if(slug === 'new') {
        return [ret[0]]
    }

    return ret;
};

const useSampleEventTools = (slug) => {
    const permission = usePermission();

    let ret = [
        {
            key: "summary",
            title: "Summary",
            component: SampleEventViewPage,
            display: true,
            isDefault: true,
            disabled: false,
        },
        // {
        //     key: "activity",
        //     title: "Activity",
        //     component: ContextNodeActivityPage,
        //     display: permission('view_sample_event_activity_feed_events'),
        //     isDefault: false,
        //     disabled: false,
        // },
    ];

    // unsaved models only have the summary tool
    if(slug === 'new') {
        return [ret[0]]
    }

    return ret;
};
